import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Paper, Stack, Typography, Alert } from '@mui/material';
import { Groups } from '@mui/icons-material';
import { liveKitService, attendanceService } from '../services/api';
import LiveKitRoomView from './LiveKitRoomView';

// Employee-facing "virtual office" widget — separate feature from the Google Meet card above it
// on the Attendance page (see LIVEKIT_SETUP.md). Rooms are project-scoped (2026-08-14): an
// employee sees one row per project they're actively assigned to, and can join that project's
// room independently of any other. canJoin/token are only ever trusted from the backend
// response; the button state here is a UX nicety, not the actual security mechanism (the backend
// re-validates project assignment + attendance on every /today/access call, including the one
// right before connecting).
//
// Join also marks attendance (2026-08-14 product decision, changed from the original "mark
// attendance first, then join" flow): if the only reason access is denied is AttendanceRequired,
// handleJoin marks attendance via the SAME endpoint the standalone "Mark Attendance Now" button
// uses, then re-checks access, so one click does both. Every other denial reason
// (LiveKitDisabled/EmployeeInactive/NotAssignedToProject) still just blocks the button — marking
// attendance can't fix those.
interface ProjectAccess {
  projectId: number;
  projectName: string;
  canJoin: boolean;
  reason?: string;
}

const reasonText = (reason?: string) => {
  switch (reason) {
    case 'AttendanceRequired': return 'Click Join to mark today\'s attendance and connect.';
    case 'EmployeeInactive': return 'Virtual office access is unavailable for this account.';
    case 'LiveKitDisabled': return 'The virtual office is not enabled yet.';
    case 'NotAssignedToProject': return 'You are not assigned to this project.';
    default: return 'Virtual office access is currently unavailable.';
  }
};

interface LiveKitPresenceProps {
  /** Called after Join auto-marks attendance, so the parent Attendance page can refresh its own
   * attendance status/button state (e.g. "Mark Attendance Now" → "Already Marked"). */
  onAttendanceMarked?: () => void;
  /** Called once the project list has (re)loaded, reporting whether this employee has any
   * Virtual Office project to join. The parent Attendance page uses this to hide its standalone
   * "Mark Today's Attendance" button when Join is available (2026-08-14: Join marks attendance
   * itself, so the separate button is redundant) — but only for employees who actually have a
   * Join option; employees with no project assignment still need the manual button. */
  onProjectsLoaded?: (hasProjects: boolean) => void;
}

const LiveKitPresence: React.FC<LiveKitPresenceProps> = ({ onAttendanceMarked, onProjectsLoaded }) => {
  const [projects, setProjects] = useState<ProjectAccess[] | null>(null);
  const [connectingProjectId, setConnectingProjectId] = useState<number | null>(null);
  const [session, setSession] = useState<{ projectId: number; token: string; serverUrl: string } | null>(null);
  const [error, setError] = useState('');

  const loadProjects = useCallback(async () => {
    try {
      const data = await liveKitService.getMyProjectsAccess();
      setProjects(data);
      onProjectsLoaded?.(data.length > 0);
    } catch {
      // LiveKit may be disabled/unavailable — don't block the attendance page on it.
      setProjects(null);
      onProjectsLoaded?.(false);
    }
  }, [onProjectsLoaded]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleJoin = async (projectId: number) => {
    setConnectingProjectId(projectId);
    setError('');
    try {
      // Re-check right before connecting — never rely on the possibly-stale state from page load.
      let fresh = await liveKitService.getTodayAccess(projectId);

      if (!fresh.canJoin && fresh.reason === 'AttendanceRequired') {
        try {
          const now = new Date();
          const checkInTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:00`;
          await attendanceService.markAttendance(checkInTime, 'Present');
          onAttendanceMarked?.();
          fresh = await liveKitService.getTodayAccess(projectId); // re-check now that attendance is marked
        } catch (markError: any) {
          setError(markError.response?.data?.message || markError.response?.data || 'Could not mark attendance automatically. Please try again.');
          loadProjects();
          return;
        }
      }

      if (fresh.canJoin && fresh.token && fresh.serverUrl) {
        setSession({ projectId, token: fresh.token, serverUrl: fresh.serverUrl });
      } else {
        setError(reasonText(fresh.reason));
        loadProjects(); // refresh in case the reason changed
      }
    } catch {
      setError('Could not verify virtual office access. Please try again.');
    } finally {
      setConnectingProjectId(null);
    }
  };

  const handleDisconnected = () => {
    setSession(null);
    loadProjects(); // refresh the list back to "join" state
  };

  // Nothing rendered when the feature is off/unreachable, or the employee has no project
  // assignments to show — matches the Google Meet card's "don't clutter the attendance page with
  // an inapplicable integration" behavior.
  if (!projects || projects.length === 0) return null;

  const activeProject = session ? projects.find(p => p.projectId === session.projectId) : null;

  if (session) {
    return (
      <Paper sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1, bgcolor: 'grey.900', color: 'white' }}>
          <Typography variant="body2">Virtual Office — {activeProject?.projectName ?? `Project #${session.projectId}`}</Typography>
        </Box>
        <LiveKitRoomView
          token={session.token}
          serverUrl={session.serverUrl}
          projectId={session.projectId}
          onDisconnected={handleDisconnected}
        />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>Virtual Office</Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      <Stack spacing={1}>
        {projects.map(p => (
          <Stack key={p.projectId} direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}
            sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
            <Box>
              <Typography variant="body1" fontWeight={500}>{p.projectName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {p.canJoin ? 'Connect to stay present with your team — time connected counts toward your availability.' : reasonText(p.reason)}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<Groups />}
              onClick={() => handleJoin(p.projectId)}
              disabled={(!p.canJoin && p.reason !== 'AttendanceRequired') || connectingProjectId === p.projectId}
            >
              {connectingProjectId === p.projectId
                ? 'Joining…'
                : p.reason === 'AttendanceRequired' ? 'Mark Attendance & Join' : 'Join'}
            </Button>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};

export default LiveKitPresence;
